import { getCurrentUser } from "@aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { useEffect, useRef, useState } from "react";

/* Dashboard data effects intentionally control their own fetch lifecycle. */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  listAssessmentAnswers,
  listAssessmentScores,
  listCreatePlans,
  listGeneralInformations,
  listNotifications,
  listOrganizationCPs,
  listOrganizationInformations,
  listProfessionalReviews,
  listTrackPlans,
  listUserLinks,
  listUserPayments,
} from "../graphql/queries.js";
import { useConnectedClient } from "../ui-components/ConnectedClientContext.js";

const rawClient = generateClient();
const client = {
  graphql: (options) => rawClient.graphql({ authMode: "userPool", ...options }),
};
const diagnosisFields = [
  "MHDxPTSD",
  "MHDxDepression",
  "MHDxSUD",
  "PHDxInfertility",
  "PHDxCurableSTD",
  "PHDxIncurableSTD",
  "PHDxCancer",
  "PHDxDiabetes",
  "PHDxHighBlood",
  "PHDxHeartDisease",
  "PHDxIrritableBowel",
  "PHDxVitA",
  "PHDxVitB",
  "PHDxVitC",
  "PHDxVitD",
  "PHDxVitE",
  "PHDxVitK",
  "PHDxAutoimmune",
  "PHDxOtherPhysical",
];
const diagnosisDisplayNames = {
  MHDxPTSD: "PTSD",
  MHDxDepression: "Depression",
  MHDxSUD: "Substance Use Disorder",
  PHDxInfertility: "Infertility",
  PHDxCurableSTD: "Curable STD",
  PHDxIncurableSTD: "Incurable STD",
  PHDxCancer: "Cancer",
  PHDxDiabetes: "Diabetes",
  PHDxHighBlood: "High Blood Pressure",
  PHDxHeartDisease: "Heart Disease",
  PHDxIrritableBowel: "Irritable Bowel Syndrome",
  PHDxVitA: "Vitamin A Deficiency",
  PHDxVitB: "Vitamin B Deficiency",
  PHDxVitC: "Vitamin C Deficiency",
  PHDxVitD: "Vitamin D Deficiency",
  PHDxVitE: "Vitamin E Deficiency",
  PHDxVitK: "Vitamin K Deficiency",
  PHDxAutoimmune: "Autoimmune Condition",
  PHDxOtherPhysical: "Other Physical Condition",
};
const traumaFields = [
  "TRHurtfulNames",
  "TRNoNurturing",
  "TRHit",
  "TRNeedsNotMet",
  "TRForcedSex",
  "TRResponsible",
  "TRViolence",
  "TRSubstanceAbuse",
  "TRMentalIllness",
  "TRParentDivorce",
  "TRParentIncarcerated",
  "TRHomelessness",
  "TRBodyFunction",
  "TRNaturalDisaster",
  "TRDirectTerrorism",
  "TRIndirectTerrorism",
  "TRLovedOne",
];

const traumaDisplayNames = {
  TRHurtfulNames: "Hurtful Names",
  TRNoNurturing: "No Nurturing",
  TRHit: "Hit",
  TRNeedsNotMet: "Needs Not Met",
  TRForcedSex: "Forced Sex",
  TRResponsible: "Responsible for Others",
  TRViolence: "Witnessed Violence",
  TRSubstanceAbuse: "Substance Abuse in Household",
  TRMentalIllness: "Mental Illness in Household",
  TRParentDivorce: "Parental Divorce",
  TRParentIncarcerated: "Parent Incarcerated",
  TRHomelessness: "Homelessness",
  TRBodyFunction: "Loss of Body Function",
  TRNaturalDisaster: "Natural Disaster",
  TRDirectTerrorism: "Direct Terrorism",
  TRIndirectTerrorism: "Indirect Terrorism",
  TRLovedOne: "Loss of Loved One",
};

const sdoHFields = [
  "SDoHAgeRange",
  "SDoHGender",
  "SDoHMaritalStatus",
  "SDoHEducation",
  "SDoHIncome",
  "SDoHJobStatus",
  "SDoHHousingStatus",
  "SDoHReligion",
];

const sdoHPieOrder = [
  "SDoHAgeRange",
  "SDoHGender",
  "SDoHMaritalStatus",
  "SDoHEducation",
  "SDoHIncome",
  "SDoHJobStatus",
  "SDoHHousingStatus",
  "SDoHReligion",
];

const sdoHDisplayNames = {
  SDoHAgeRange: "Age Range",
  SDoHGender: "Gender",
  SDoHMaritalStatus: "Marital Status",
  SDoHEducation: "Education",
  SDoHIncome: "Income",
  SDoHJobStatus: "Job Status",
  SDoHHousingStatus: "Housing Status",
  SDoHReligion: "Religion",
};
export default function DashboardOrganization() {
  const { connectedClient, setConnectedClient } = useConnectedClient();

  const linkIntervalsByClientIdRef = useRef(new Map());

  const parseInputDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = String(dateStr).split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const buildSelectedDateRange = () => {
    const start = startDate ? parseInputDate(startDate) : null;
    const end = endDate ? parseInputDate(endDate) : null;
    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);
    return {
      startMs: start ? start.getTime() : null,
      endMs: end ? end.getTime() : null,
    };
  };

  const toMs = (value) => {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  const isMsWithinRange = (ms, range) => {
    if (!Number.isFinite(ms)) return false;
    if (!range) return true;
    if (range.startMs != null && ms < range.startMs) return false;
    if (range.endMs != null && ms > range.endMs) return false;
    return true;
  };

  const isMsWithinAnyInterval = (ms, intervals) => {
    if (!Number.isFinite(ms)) return false;
    if (!Array.isArray(intervals) || intervals.length === 0) return true;
    for (const i of intervals) {
      const startMs = toMs(i?.start);
      if (!startMs) continue;
      const endMs = i?.end ? toMs(i.end) : null;
      const effectiveEnd = endMs ?? Number.POSITIVE_INFINITY;
      if (ms >= startMs && ms <= effectiveEnd) return true;
    }
    return false;
  };

  const intervalOverlapsRange = (interval, range) => {
    if (!interval) return false;
    const startMs = toMs(interval.start);
    if (!startMs) return false;
    const endMs = interval.end ? toMs(interval.end) : null;

    const effectiveEnd = endMs ?? Number.POSITIVE_INFINITY;
    const effectiveRangeStart = range?.startMs ?? Number.NEGATIVE_INFINITY;
    const effectiveRangeEnd = range?.endMs ?? Number.POSITIVE_INFINITY;

    return startMs <= effectiveRangeEnd && effectiveEnd >= effectiveRangeStart;
  };

  const parseCompletedDateTimeMs = (completedDateRaw, completedTimeRaw) => {
    const date = String(completedDateRaw || "").trim();
    if (!date) return null;
    let normalizedDate = "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      normalizedDate = date;
    } else {
      const mdy = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mdy) {
        const mm = String(mdy[1]).padStart(2, "0");
        const dd = String(mdy[2]).padStart(2, "0");
        const yyyy = mdy[3];
        normalizedDate = `${yyyy}-${mm}-${dd}`;
      }
    }
    if (!normalizedDate) return null;

    const time = String(completedTimeRaw || "").trim();
    let normalizedTime = "";
    if (time && /^\d{1,2}:\d{2}(:\d{2})?$/.test(time)) {
      normalizedTime = time.length === 5 ? `${time}:00` : time;
    }

    const iso = normalizedTime
      ? `${normalizedDate}T${normalizedTime}`
      : `${normalizedDate}T00:00:00`;
    const ms = new Date(iso).getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  const getAssessmentLikeMs = (item) => {
    const completedMs = parseCompletedDateTimeMs(
      item?.CompletedDate,
      item?.CompletedTime,
    );
    if (completedMs != null) return completedMs;
    return toMs(item?.createdAt) ?? toMs(item?.updatedAt) ?? null;
  };

  const fetchAllPages = async ({
    query,
    dataKey,
    variables,
    pageSize = 1000,
  }) => {
    const items = [];
    let nextToken = null;
    do {
      const res = await client.graphql({
        query,
        variables: { ...(variables || {}), limit: pageSize, nextToken },
      });
      const pageItems = res?.data?.[dataKey]?.items || [];
      items.push(...pageItems);
      nextToken = res?.data?.[dataKey]?.nextToken || null;
    } while (nextToken);
    return items;
  };

  const getCurrentOrganizationContext = async () => {
    const user = await getCurrentUser();
    const baseIdentifiers = [user?.userId, user?.username]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    const allOrgInfos = await fetchAllPages({
      query: listOrganizationInformations,
      dataKey: "listOrganizationInformations",
    });

    const matchedOrgInfos = allOrgInfos.filter((info) => {
      const candidates = [info?.userId, info?.organizationId, info?.id]
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      return candidates.some((value) => baseIdentifiers.includes(value));
    });

    const currentOrgInfo = matchedOrgInfos[0] || null;
    const orgIdentifierSet = new Set(baseIdentifiers);
    for (const info of matchedOrgInfos) {
      for (const value of [info?.userId, info?.organizationId, info?.id]) {
        const normalized = String(value || "").trim();
        if (normalized) orgIdentifierSet.add(normalized);
      }
    }

    return { currentOrgInfo, orgIdentifierSet };
  };
  const [professionals, setProfessionals] = useState([]);
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("clients");
  const [filtered, setFiltered] = useState([]);
  const [filteredClientsForStats, setFilteredClientsForStats] = useState([]);
  const [clientFilterPro, setClientFilterPro] = useState("");
  const [topDiagnoses, setTopDiagnoses] = useState([]);
  const [topActions, setTopActions] = useState([]);
  const [userListPageSize, setUserListPageSize] = useState(10);
  const [userListPage, setUserListPage] = useState(1);
  const TOP5_SECTIONS = [
    { key: "STRENGTHS", label: "Strengths" },
    { key: "BARRIERS", label: "Barriers" },
    { key: "WARNING_SIGNS", label: "Warning Signs" },
    { key: "TRIGGERS", label: "Triggers" },
    { key: "POS_COPING", label: "Positive Coping" },
    { key: "NEG_COPING", label: "Negative Coping" },
    { key: "PHYSICAL_DX", label: "Physical Diagnoses" },
    { key: "MENTAL_DX", label: "Mental Diagnoses" },
    { key: "OUTSIDE_RANGE_MH", label: "Outside a Healthy Range - MH" },
    { key: "OUTSIDE_RANGE_PH", label: "Outside a Healthy Range - PH" },
    { key: "OUTSIDE_RANGE_SH", label: "Outside a Healthy Range - SH" },
    { key: "OUTSIDE_RANGE_SOH", label: "Outside a Healthy Range - SoH" },
    { key: "TRAUMAS", label: "Traumas" },
    { key: "ACTIONS_CHOSEN", label: "Actions Chosen" },
    { key: "SUCCESSFUL_ACTIONS", label: "Successful Actions" },
    { key: "UNSUCCESSFUL_ACTIONS", label: "Unsuccessful Actions" },
  ];

  const [openTop5Sections, setOpenTop5Sections] = useState({});
  const [top5Stats, setTop5Stats] = useState({
    TRAUMAS: [],
    STRENGTHS: [],
    BARRIERS: [],
    WARNING_SIGNS: [],
    TRIGGERS: [],
    ACTIONS_CHOSEN: [],
    SUCCESSFUL_ACTIONS: [],
    UNSUCCESSFUL_ACTIONS: [],
    POS_COPING: [],
    NEG_COPING: [],
    OUTSIDE_RANGE_MH: [],
    OUTSIDE_RANGE_PH: [],
    OUTSIDE_RANGE_SH: [],
    OUTSIDE_RANGE_SOH: [],
    PHYSICAL_DX: [],
    MENTAL_DX: [],
  });

  const toggleTop5Section = (key) => {
    setOpenTop5Sections((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  const formatTop5Count = (sectionKey, value) => {
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num) || num <= 0) return "--";
    const pct = Math.round(num * 1000) / 10;
    const pretty = Number.isInteger(pct) ? String(pct) : String(pct);
    return `${pretty}%`;
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [pendingStartDate, setPendingStartDate] = useState("");
  const [pendingEndDate, setPendingEndDate] = useState("");
  const [departments, setDepartments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [services, setServices] = useState([]);

  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterProgram, setFilterProgram] = useState("");
  const [filterService, setFilterService] = useState("");
  const [generalInformation, setGeneralInformation] = useState([]);
  const [generalInfoLoaded, setGeneralInfoLoaded] = useState(false);

  const [sdoHStats, setSdoHStats] = useState({
    byField: {
      SDoHAgeRange: { total: 0, counts: {} },
      SDoHGender: { total: 0, counts: {} },
      SDoHMaritalStatus: { total: 0, counts: {} },
      SDoHEducation: { total: 0, counts: {} },
      SDoHIncome: { total: 0, counts: {} },
      SDoHJobStatus: { total: 0, counts: {} },
      SDoHHousingStatus: { total: 0, counts: {} },
      SDoHReligion: { total: 0, counts: {} },
    },
  });

  // Palette: user-specified base colors + generated shades for additional categories.
  const sdoHPieBasePalette = [
    "#0067B3",
    "#8C1456",
    "#2F5D50",
    "#D4B65F",
    "#6F8F7B",
  ];

  const adjustHex = (hex, amount) => {
    const h = hex.replace("#", "");
    const num = parseInt(h, 16);
    const r = Math.max(0, Math.min(255, (num >> 16) + amount));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
    const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const buildExtendedPalette = (base) => {
    const shades = [];
    for (const c of base) {
      shades.push(c);
      shades.push(adjustHex(c, 35));
      shades.push(adjustHex(c, 70));
      shades.push(adjustHex(c, -25));
    }
    return shades;
  };

  const sdoHPiePalette = buildExtendedPalette(sdoHPieBasePalette);

  const buildPieSegments = (counts, maxSlices = 6) => {
    const entries = Object.entries(counts || {}).filter(([, c]) => c > 0);
    const total = entries.reduce((sum, [, c]) => sum + c, 0);
    if (!total) return [];

    entries.sort((a, b) => b[1] - a[1]);

    const keep = Math.max(1, Math.min(maxSlices - 1, entries.length));
    const kept = entries.slice(0, keep);
    const remainder = entries.slice(keep);
    const otherCount = remainder.reduce((sum, [, c]) => sum + c, 0);

    const segments = kept.map(([label, count], idx) => ({
      label,
      count,
      percent: (count / total) * 100,
      color: sdoHPiePalette[idx % sdoHPiePalette.length],
    }));

    if (otherCount > 0) {
      segments.push({
        label: "Other",
        count: otherCount,
        percent: (otherCount / total) * 100,
        color: sdoHPiePalette[segments.length % sdoHPiePalette.length],
      });
    }

    return segments;
  };

  const pieStyleFromSegments = (segments) => {
    if (!segments || segments.length === 0) return { background: "#f9f9f9" };

    let cursor = 0;
    const stops = segments
      .map((s) => {
        const start = cursor;
        cursor += s.percent;
        const end = cursor;
        return `${s.color} ${start.toFixed(2)}% ${end.toFixed(2)}%`;
      })
      .join(", ");

    return {
      background: `conic-gradient(${stops})`,
    };
  };

  useEffect(() => {
    const fetchGeneralInformation = async () => {
      try {
        const items = await fetchAllPages({
          query: listGeneralInformations,
          dataKey: "listGeneralInformations",
        });
        setGeneralInformation(items);
        setGeneralInfoLoaded(true);
      } catch (err) {
        console.error("Error fetching general information:", err);
        setGeneralInfoLoaded(true);
      }
    };

    fetchGeneralInformation();
  }, []);

  // Bootstrap org-scoped clients as soon as we have user context.
  // Professional records are enrichment, not a prerequisite for seeing clients.
  useEffect(() => {
    if (!generalInfoLoaded) return;

    const loadOrganizationDashboard = async () => {
      try {
        const { orgIdentifierSet } = await getCurrentOrganizationContext();
        await fetchOrganizationInfo();
        await fetchAllClients([], orgIdentifierSet);
        await fetchProfessionals();
      } catch (err) {
        console.error("Failed to bootstrap organization dashboard:", err);
      }
    };

    loadOrganizationDashboard();
  }, [generalInfoLoaded]);

  const getUserInfoById = (userId) => {
    if (!generalInformation || generalInformation.length === 0) return {};
    return (
      generalInformation.find(
        (u) => u.userId === userId || u.owner === userId,
      ) || {}
    );
  };
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    setUserListPage(1);
  }, [
    search,
    clientFilterPro,
    startDate,
    endDate,
    filterDepartment,
    filterProgram,
    filterService,
    connectedClient,
    view,
  ]);
  const totalUserListPages = Math.max(
    1,
    Math.ceil((filtered.length || 0) / userListPageSize),
  );
  const safeUserListPage = Math.min(userListPage, totalUserListPages);
  const paginatedFiltered = filtered.slice(
    (safeUserListPage - 1) * userListPageSize,
    safeUserListPage * userListPageSize,
  );
  const containerStyle2 = {
    flexDirection: "column",
    alignItems: "center",
    width: isMobile ? "90%" : "1000px",
    margin: "1px auto",
    padding: 24,
    borderRadius: 12,
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    display: "block",
  };
  const statsContainerResponsive = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: isMobile ? "center" : "flex-start", // center on mobile, align start on desktop
    width: isMobile ? "90%" : "1000px",
    margin: "24px auto",
    gap: isMobile ? 12 : 24,
    flexDirection: isMobile ? "column" : "row",
  };
  const sideBoxStyle = {
    flex: "1",
    padding: 20,
    width: isMobile ? "100%" : "auto",
    borderRadius: 12,
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    minHeight: 350,
  };

  const centerBoxStyle = {
    flex: "1.3",
    padding: 20,
    width: isMobile ? "100%" : "auto",
    borderRadius: 12,
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    minHeight: 350,
  };

  const [topTraumas, setTopTraumas] = useState([]);
  const [individualClientStats, setIndividualClientStats] = useState({
    totalAssessments: 0,
    totalInProgress: 0,
    totalDiscontinued: 0,
    totalSuccessful: 0,
  });
  const [collectiveStats, setCollectiveStats] = useState({
    totalAssessments: 0,
    totalInProgress: 0,
    totalDiscontinued: 0,
    totalSuccessful: 0,
  });

  const fetchTopTraumas = async (clientsList) => {
    try {
      const traumaCount = {};

      const range = buildSelectedDateRange();

      for (const user of clientsList) {
        const intervals =
          linkIntervalsByClientIdRef.current?.get?.(user?.clientId) || [];

        const { data, errors } = await client.graphql({
          query: listAssessmentAnswers,
          variables: {
            filter: {
              and: [
                {
                  or: [
                    { owner: { eq: user.clientId } },
                    { userId: { eq: user.clientId } },
                  ],
                },
                {
                  or: [
                    { CompletedDate: { attributeExists: true } },
                    { isActive: { eq: false } },
                  ],
                },
              ],
            },
            limit: 1000,
          },
        });

        if (errors?.length) {
          console.error("listAssessmentAnswers (TopTraumas) errors:", errors);
        }

        const assessments = data?.listAssessmentAnswers?.items || [];
        const allowedByWindow = (ms) =>
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals);

        const eligible = assessments.filter((a) =>
          allowedByWindow(getAssessmentLikeMs(a)),
        );
        if (eligible.length === 0) continue;

        // Use the latest assessment within the selected date range + link window
        eligible.sort(
          (a, b) =>
            (getAssessmentLikeMs(a) ?? 0) - (getAssessmentLikeMs(b) ?? 0),
        );
        const latest = eligible[eligible.length - 1];

        // Count trauma fields that are true
        for (const field of traumaFields) {
          if (latest[field] === true) {
            traumaCount[field] = (traumaCount[field] || 0) + 1;
          }
        }
      }

      const sorted = Object.entries(traumaCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      setTopTraumas(sorted);
    } catch (err) {
      console.error("Failed to fetch top traumas:", err);
      setTopTraumas([]);
    }
  };
  const [diagnosisView, setDiagnosisView] = useState("count"); // or whatever toggle you use
  const [actionView, setActionView] = useState("count");

  const [improvementStats, setImprovementStats] = useState({
    CM: "--",
    MH: "--",
    PH: "--",
    SH: "--",
    SoH: "--",
    OS: "--",
  });
  const [recommendationStats, setRecommendationStats] = useState({
    recommended: 0,
    accepted: 0,
  });
  const fetchTopDiagnosisAndActions = async (clientsList) => {
    try {
      // Maps to count occurrences
      const diagnosisCounts = {};
      const actionCounts = {};

      // Collect all client IDs to fetch their data
      const clientIds = clientsList.map((c) => c.clientId);

      // For performance, you might want batch calls or pagination but here's the basic way:

      // Fetch diagnoses - assuming you have a query like listAssessmentAnswers or similar
      // Adjust according to your actual schema and diagnosis field
      for (const clientId of clientIds) {
        const diagnosisRes = await client.graphql({
          query: listAssessmentAnswers, // or your diagnoses query
          variables: {
            filter: {
              and: [
                {
                  or: [
                    { owner: { eq: clientId } },
                    { userId: { eq: clientId } },
                  ],
                },
                {
                  or: [
                    { CompletedDate: { attributeExists: true } },
                    { isActive: { eq: false } },
                  ],
                },
              ],
            },
            limit: 1000,
          },
        });

        if (diagnosisRes?.errors?.length) {
          console.error(
            "listAssessmentAnswers (TopDiagnoses) errors:",
            diagnosisRes.errors,
          );
        }

        const diagnoses =
          diagnosisRes?.data?.listAssessmentAnswers?.items || [];

        if (diagnoses.length > 0) {
          // Sort descending by creation date (latest first)
          diagnoses.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          );

          // Take the most recent assessment
          const mostRecent = diagnoses[0];

          // Count diagnosis fields from that most recent assessment
          diagnosisFields.forEach((field) => {
            if (mostRecent[field]) {
              diagnosisCounts[field] = (diagnosisCounts[field] || 0) + 1;
            }
          });
        }

        // Fetch actions from TrackPlans
        const actionsRes = await client.graphql({
          query: listTrackPlans,
          variables: {
            filter: {
              or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
            },
            limit: 1000,
          },
        });

        if (actionsRes?.errors?.length) {
          console.error(
            "listTrackPlans (TopActions) errors:",
            actionsRes.errors,
          );
        }

        const actions = actionsRes?.data?.listTrackPlans?.items || [];

        actions.forEach((plan) => {
          const actionName = plan.Action || "Unknown";
          actionCounts[actionName] = (actionCounts[actionName] || 0) + 1;
        });
      }

      // Sort and slice top 5 diagnoses
      const sortedDiagnoses = Object.entries(diagnosisCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));

      // Sort and slice top 5 actions
      const sortedActions = Object.entries(actionCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));

      setTopDiagnoses(sortedDiagnoses);
      setTopActions(sortedActions);
    } catch (err) {
      console.error("Failed to fetch top diagnoses and actions:", err);
      setTopDiagnoses([]);
      setTopActions([]);
    }
  };

  const buildCreatePlanLabelMap = () => {
    const entries = [
      // Strengths
      ["PSProblemSolving", "Problem-Solving Skills"],
      ["PSSelfEsteem", "Self-Esteem and Confidence"],
      ["PSChoiceAutonomy", "Choice Autonomy"],
      ["PSSelfCare", "Self-Care"],
      ["PSSelfAwareness", "Self-Awareness"],
      ["PSCreativity", "Creativity"],
      ["CSForgiveness", "Forgiveness"],
      ["CSKindness", "Kindness"],
      ["CSGratitude", "Gratitude"],
      ["CSPatience", "Patience"],
      ["CSCourage", "Courage"],
      ["CMSCopingChoice", "Coping Mechanisms"],
      ["CMSResilience", "Resilience and Perseverance"],
      ["CMSSelfRegulation", "Self-Regulation"],
      ["CMSOptimism", "Hope and Optimism"],
      ["COMSupportSystem", "Strong Support System"],
      ["COMSocialIntelligence", "Social Intelligence"],
      ["COMCommunication", "Communication Skills"],
      ["COMFaithCommunity", "Faith Based Community"],
      ["COMAccomplishments", "Accomplishments"],

      // Barriers
      ["BRStableHousing", "Access to Safe Stable Housing"],
      ["BRQualityHealthcare", "Access to Quality Healthcare"],
      ["BRProfessionalAccess", "Access to Clinical Professionals"],
      ["BRInsuranceCoverage", "Insurance Coverage"],
      ["BRFinancialConstraints", "Financial Constraints"],
      ["BREducationAttainment", "Education Attainment"],
      ["BRSocialStigma", "Social Stigma and Misconceptions"],
      ["BRUnsupportiveCommunity", "Unsupportive Personal Community"],
      ["BRNonexistentCommunity", "Nonexistent Personal Community"],
      ["BRCommunityResources", "Awareness of Community Resources"],
      ["BRProfessionalTrust", "Personal Trust in Clinical Professionals"],
      ["BRHealthcareSystemTrust", "Personal Trust in Healthcare System"],
      [
        "BRCognitiveImpairments",
        "Cognitive Impairments (e.g., memory, concentration)",
      ],
      ["BRDependentCare", "Child / Elder Care"],
      ["BRTransportation", "Transportation"],
      ["BRTechnologyAccess", "Access to Technology"],
      ["BRLanguage", "Language or Communication"],
      ["BRHealthcareProcess", "Navigating Healthcare Processes"],
      ["BROther", "Other"],

      // Triggers
      ["TGBetrayal", "Betrayal"],
      ["TGBoundaries", "Ignored Boundaries"],
      ["TGDisorder", "Loud Noise / Disorder / Chaos"],
      ["TGPain", "Pain (Emotional or Physical)"],
      ["TGRejection", "Rejection"],
      ["TGStress", "Stressful Life Events"],
      ["TGMemories", "Traumatic Memories"],
      ["TGUnjustTreatment", "Unjust Treatment"],
      [
        "TGUnpleasantConversations",
        "Unpleasant Conversations (e.g. yelling, criticism, etc.)",
      ],
      ["TGUnsafeEnvironments", "Unsafe Environments"],
      ["TGOther", "Other"],

      // Warning Signs
      ["WSFlashbacks", "Flashbacks"],
      ["WSIncreasedAppetite", "Increased Appetite"],
      ["WSDecreasedAppetite", "Decreased Appetite"],
      ["WSIntenseEmotions", "Intense Emotions / Irritability"],
      ["WSDisconnecting", "Isolation / Disconnecting"],
      ["WSTime", "Losing Track of Time"],
      ["WSNegativeSelfTalk", "Negative Self Talk"],
      ["WSNeglectingSelfCare", "Neglecting Self Care"],
      ["WSNightmares", "Nightmares"],
      ["WSRecklessBehavior", "Reckless / Destructive Behavior"],
      ["WSOther", "Other"],
    ];

    return entries.reduce((acc, [key, label]) => {
      acc[key] = label;
      return acc;
    }, {});
  };

  const fetchTop5Stats = async (clientsList) => {
    try {
      const createPlanLabelMap = buildCreatePlanLabelMap();
      const successfulActionCounts = {};
      const unsuccessfulActionCounts = {};

      const range = buildSelectedDateRange();

      const isChecked = (value) => {
        if (value === true) return true;
        if (value === 1 || value === "1") return true;
        if (typeof value === "string") {
          const v = value.trim().toLowerCase();
          return v === "true" || v === "yes" || v === "y" || v === "on";
        }
        return false;
      };

      const strengthKeys = [
        "PSProblemSolving",
        "PSSelfEsteem",
        "PSChoiceAutonomy",
        "PSSelfCare",
        "PSSelfAwareness",
        "PSCreativity",
        "CSForgiveness",
        "CSKindness",
        "CSGratitude",
        "CSPatience",
        "CSCourage",
        "CMSCopingChoice",
        "CMSResilience",
        "CMSSelfRegulation",
        "CMSOptimism",
        "COMSupportSystem",
        "COMSocialIntelligence",
        "COMCommunication",
        "COMFaithCommunity",
        "COMAccomplishments",
      ];
      const barrierKeys = [
        "BRStableHousing",
        "BRQualityHealthcare",
        "BRProfessionalAccess",
        "BRInsuranceCoverage",
        "BRFinancialConstraints",
        "BREducationAttainment",
        "BRSocialStigma",
        "BRUnsupportiveCommunity",
        "BRNonexistentCommunity",
        "BRCommunityResources",
        "BRProfessionalTrust",
        "BRHealthcareSystemTrust",
        "BRCognitiveImpairments",
        "BRDependentCare",
        "BRTransportation",
        "BRTechnologyAccess",
        "BRLanguage",
        "BRHealthcareProcess",
        "BROther",
      ];
      const triggerKeys = [
        "TGBetrayal",
        "TGBoundaries",
        "TGDisorder",
        "TGPain",
        "TGRejection",
        "TGStress",
        "TGMemories",
        "TGUnjustTreatment",
        "TGUnpleasantConversations",
        "TGUnsafeEnvironments",
        "TGOther",
      ];
      const warningSignKeys = [
        "WSFlashbacks",
        "WSIncreasedAppetite",
        "WSDecreasedAppetite",
        "WSIntenseEmotions",
        "WSDisconnecting",
        "WSTime",
        "WSNegativeSelfTalk",
        "WSNeglectingSelfCare",
        "WSNightmares",
        "WSRecklessBehavior",
        "WSOther",
      ];

      const negativeCopingFields = [
        "CMAlcohol",
        "CMDrugs",
        "CMOvereating",
        "CMSmoking",
        "CMGaming",
        "CMShopping",
        "CMGambling",
        "CMSex",
        "CMPorn",
        "CMBlaming",
        "CMHurting",
        "CMDisengage",
      ];
      const positiveCopingFields = [
        "CMArt",
        "CMMusic",
        "CMPoetry",
        "CMReading",
        "CMGroups",
        "CMCounseling",
        "CMVenting",
        "CMWriting",
        "CMSensory",
        "CMDancing",
        "CMExercising",
        "CMWalking",
        "CMChange",
        "CMAnalyze",
        "CMDaydream",
        "CMPositive",
      ];
      const copingQuestionLabels = {
        // Negative coping
        CMAlcohol: "Alcohol",
        CMDrugs: "Drugs",
        CMOvereating: "Overeating",
        CMSmoking: "Smoking",
        CMGaming: "Gaming",
        CMShopping: "Shopping",
        CMGambling: "Gambling",
        CMSex: "Casual Sex",
        CMPorn: "Pornography",
        CMBlaming: "Blaming Others",
        CMHurting: "Hurting Yourself",
        CMDisengage: "Disengaging",

        // Positive coping
        CMArt: "Art",
        CMMusic: "Music",
        CMPoetry: "Poetry",
        CMReading: "Reading",
        CMGroups: "Groups",
        CMCounseling: "Counseling",
        CMVenting: "Venting",
        CMWriting: "Writing",
        CMSensory: "Sensory Methods",
        CMDancing: "Dancing",
        CMExercising: "Exercising",
        CMWalking: "Walking",
        CMChange: "Changing Environment",
        CMAnalyze: "Analyzing Situation",
        CMDaydream: "Daydreaming",
        CMPositive: "Positive Thinking",
      };

      const mentalDxFields = ["MHDxPTSD", "MHDxDepression", "MHDxSUD"];
      const mentalDxDisplayNames = {
        MHDxPTSD: "PTSD",
        MHDxDepression: "Depression",
        MHDxSUD: "Substance Use Disorder",
      };
      const physicalDxFields = diagnosisFields.filter(
        (k) => !mentalDxFields.includes(k),
      );

      const traumaCounts = {};
      const strengthCounts = {};
      const barrierCounts = {};
      const warningSignCounts = {};
      const triggerCounts = {};
      const actionsChosenCounts = {};
      const posCopingCounts = {};
      const negCopingCounts = {};
      const outsideRangeMHCounts = {};
      const outsideRangePHCounts = {};
      const outsideRangeSHCounts = {};
      const outsideRangeSoHCounts = {};
      const physicalDxCounts = {};
      const mentalDxCounts = {};

      const scoreExcludedKeys = new Set([
        "id",
        "userId",
        "CompletedDate",
        "CompletedTime",
        "createdAt",
        "updatedAt",
        "owner",
        "__typename",
        "isActive",
        "SHSkip",
      ]);
      const isScoreQuestionKey = (key) => {
        if (scoreExcludedKeys.has(key)) return false;
        if (key.endsWith("Score")) return false;
        if (key.startsWith("SDoH")) return false;
        return true;
      };

      const formatAssessmentScoreQuestionText = (key) => {
        if (typeof key !== "string") return String(key ?? "");

        const prefixes = ["SoH", "CM", "PH", "MH", "SH"];
        const prefix = prefixes.find((p) => key.startsWith(p));
        const rest = prefix ? key.slice(prefix.length) : key;
        if (!rest) return key;

        const readable = rest
          .replace(/_/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\s+/g, " ")
          .trim();

        return readable || key;
      };

      const parseTimesPerWeek = (raw) => {
        if (raw == null) return null;
        const text = String(raw).trim().toLowerCase();
        if (!text) return null;
        if (text === "0" || text.includes("never") || text.includes("none"))
          return null;

        if (text.includes("daily") || text === "every day") return 7;
        if (text.includes("weekly") || text === "every week") return 1;
        if (text.includes("monthly") || text === "every month") return 1 / 4;

        const unitMatch = text.match(
          /([0-9]+(?:\.[0-9]+)?)\s*(?:x|times)?\s*(?:per\s*)?(day|week|month)/,
        );
        if (unitMatch) {
          const value = Number(unitMatch[1]);
          const unit = unitMatch[2];
          if (!Number.isFinite(value) || value <= 0) return null;
          if (unit === "week") return value;
          if (unit === "day") return value * 7;
          if (unit === "month") return value / 4;
        }

        const numberOnly = Number(text.replace(/[^0-9.]/g, ""));
        if (Number.isFinite(numberOnly) && numberOnly > 0) return numberOnly;

        return null;
      };

      const pickMostRecentByMs = (items, getMs) => {
        const sorted = [...(items || [])].sort((a, b) => {
          const dA = getMs(a) ?? 0;
          const dB = getMs(b) ?? 0;
          if (dA !== dB) return dB - dA;
          const uA = toMs(a?.updatedAt) ?? 0;
          const uB = toMs(b?.updatedAt) ?? 0;
          if (uA !== uB) return uB - uA;
          return String(a?.id || "").localeCompare(String(b?.id || ""));
        });
        return sorted[0] || null;
      };

      const uniqueClientIds = new Set();
      const uniqueClientsList = [];
      for (const user of clientsList || []) {
        const id = user?.clientId;
        if (!id || uniqueClientIds.has(id)) continue;
        uniqueClientIds.add(id);
        uniqueClientsList.push(user);
      }

      const totalUnduplicatedClients = uniqueClientsList.length;

      for (const user of uniqueClientsList) {
        const clientId = user?.clientId;
        if (!clientId) continue;

        const intervals =
          linkIntervalsByClientIdRef.current?.get?.(clientId) || [];
        const allowedByWindow = (ms) =>
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals);

        const [assessmentsRes, scoresRes, trackPlansRes, createPlansRes] =
          await Promise.all([
            client.graphql({
              query: listAssessmentAnswers,
              variables: {
                filter: {
                  and: [
                    {
                      or: [
                        { owner: { eq: clientId } },
                        { userId: { eq: clientId } },
                      ],
                    },
                    {
                      or: [
                        { CompletedDate: { attributeExists: true } },
                        { isActive: { eq: false } },
                      ],
                    },
                  ],
                },
                limit: 1000,
              },
            }),
            client.graphql({
              query: listAssessmentScores,
              variables: {
                filter: {
                  and: [
                    {
                      or: [
                        { owner: { eq: clientId } },
                        { userId: { eq: clientId } },
                      ],
                    },
                    {
                      or: [
                        { CompletedDate: { attributeExists: true } },
                        { isActive: { eq: false } },
                      ],
                    },
                  ],
                },
                limit: 1000,
              },
            }),
            client.graphql({
              query: listTrackPlans,
              variables: {
                filter: {
                  or: [
                    { owner: { eq: clientId } },
                    { userId: { eq: clientId } },
                  ],
                },
                limit: 1000,
              },
            }),
            client.graphql({
              query: listCreatePlans,
              variables: {
                filter: {
                  userId: { eq: clientId },
                },
                limit: 100,
              },
            }),
          ]);

        const assessmentsRaw =
          assessmentsRes?.data?.listAssessmentAnswers?.items || [];
        const scoresRaw = scoresRes?.data?.listAssessmentScores?.items || [];
        const trackPlansRaw = trackPlansRes?.data?.listTrackPlans?.items || [];
        const createPlansRaw =
          createPlansRes?.data?.listCreatePlans?.items || [];

        const assessments = assessmentsRaw.filter((a) =>
          allowedByWindow(getAssessmentLikeMs(a)),
        );
        const scores = scoresRaw.filter((s) =>
          allowedByWindow(getAssessmentLikeMs(s)),
        );
        const trackPlans = trackPlansRaw.filter((p) =>
          allowedByWindow(toMs(p?.createdAt)),
        );
        const createPlans = createPlansRaw.filter((p) =>
          allowedByWindow(toMs(p?.createdAt)),
        );

        const latestAssessment = assessments.length
          ? pickMostRecentByMs(assessments, getAssessmentLikeMs)
          : null;
        const latestScores = scores.length
          ? pickMostRecentByMs(scores, getAssessmentLikeMs)
          : null;
        const latestCreatePlan = createPlans.length
          ? pickMostRecentByMs(createPlans, (p) => toMs(p?.createdAt))
          : null;

        if (latestAssessment) {
          for (const field of traumaFields) {
            if (latestAssessment?.[field] === true) {
              const label = traumaDisplayNames[field] || field;
              traumaCounts[label] = (traumaCounts[label] || 0) + 1;
            }
          }

          for (const field of positiveCopingFields) {
            const timesPerWeek = parseTimesPerWeek(latestAssessment?.[field]);
            if (!timesPerWeek) continue;
            const label = copingQuestionLabels[field] || field;
            posCopingCounts[label] = (posCopingCounts[label] || 0) + 1;
          }
          for (const field of negativeCopingFields) {
            const timesPerWeek = parseTimesPerWeek(latestAssessment?.[field]);
            if (!timesPerWeek) continue;
            const label = copingQuestionLabels[field] || field;
            negCopingCounts[label] = (negCopingCounts[label] || 0) + 1;
          }

          for (const field of physicalDxFields) {
            if (latestAssessment?.[field]) {
              const label = diagnosisDisplayNames[field] || field;
              physicalDxCounts[label] = (physicalDxCounts[label] || 0) + 1;
            }
          }
          for (const field of mentalDxFields) {
            if (latestAssessment?.[field]) {
              const label = mentalDxDisplayNames[field] || field;
              mentalDxCounts[label] = (mentalDxCounts[label] || 0) + 1;
            }
          }
        }

        if (latestScores) {
          for (const [key, raw] of Object.entries(latestScores)) {
            if (!isScoreQuestionKey(key)) continue;
            const num = typeof raw === "number" ? raw : Number(raw);
            const label = formatAssessmentScoreQuestionText(key);
            if (num === 3 || num === 4) {
              if (key.startsWith("MH")) {
                outsideRangeMHCounts[label] =
                  (outsideRangeMHCounts[label] || 0) + 1;
              } else if (key.startsWith("PH")) {
                outsideRangePHCounts[label] =
                  (outsideRangePHCounts[label] || 0) + 1;
              } else if (key.startsWith("SH")) {
                outsideRangeSHCounts[label] =
                  (outsideRangeSHCounts[label] || 0) + 1;
              } else if (key.startsWith("SoH")) {
                outsideRangeSoHCounts[label] =
                  (outsideRangeSoHCounts[label] || 0) + 1;
              }
            }
          }
        }

        if (trackPlans.length) {
          const latestPlanMs = toMs(latestCreatePlan?.createdAt);
          const filteredByLatestPlan = latestPlanMs
            ? trackPlans.filter((p) => {
                const ms = toMs(p?.createdAt) ?? 0;
                return ms >= latestPlanMs;
              })
            : trackPlans;

          const relevantTrackPlans = filteredByLatestPlan.length
            ? filteredByLatestPlan
            : trackPlans;

          const uniqueActions = new Set();
          for (const plan of relevantTrackPlans) {
            const action = plan?.Action ? String(plan.Action).trim() : "";
            if (action) uniqueActions.add(action);
          }
          for (const action of uniqueActions) {
            actionsChosenCounts[action] =
              (actionsChosenCounts[action] || 0) + 1;
          }

          const successfulActionsForClient = new Set();
          const unsuccessfulActionsForClient = new Set();
          for (const plan of relevantTrackPlans) {
            const action = plan?.Action ? String(plan.Action).trim() : "";
            if (!action) continue;
            if (plan?.Successful) successfulActionsForClient.add(action);
            if (plan?.Discontinued) unsuccessfulActionsForClient.add(action);
          }
          for (const action of successfulActionsForClient) {
            successfulActionCounts[action] =
              (successfulActionCounts[action] || 0) + 1;
          }
          for (const action of unsuccessfulActionsForClient) {
            unsuccessfulActionCounts[action] =
              (unsuccessfulActionCounts[action] || 0) + 1;
          }
        }

        if (latestCreatePlan) {
          for (const key of strengthKeys) {
            if (isChecked(latestCreatePlan?.[key])) {
              const label = createPlanLabelMap[key] || key;
              strengthCounts[label] = (strengthCounts[label] || 0) + 1;
            }
          }
          for (const key of barrierKeys) {
            if (isChecked(latestCreatePlan?.[key])) {
              const label = createPlanLabelMap[key] || key;
              barrierCounts[label] = (barrierCounts[label] || 0) + 1;
            }
          }
          for (const key of warningSignKeys) {
            if (isChecked(latestCreatePlan?.[key])) {
              const label = createPlanLabelMap[key] || key;
              warningSignCounts[label] = (warningSignCounts[label] || 0) + 1;
            }
          }
          for (const key of triggerKeys) {
            if (isChecked(latestCreatePlan?.[key])) {
              const label = createPlanLabelMap[key] || key;
              triggerCounts[label] = (triggerCounts[label] || 0) + 1;
            }
          }
        }
      }

      const toTop5Rate = (hits, denominator) =>
        Object.entries(hits)
          .map(([label, count]) => {
            const rate = denominator > 0 ? count / denominator : 0;
            return { label, count: rate };
          })
          .filter((x) => Number.isFinite(x.count) && x.count > 0)
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

      setTop5Stats({
        TRAUMAS: toTop5Rate(traumaCounts, totalUnduplicatedClients),
        STRENGTHS: toTop5Rate(strengthCounts, totalUnduplicatedClients),
        BARRIERS: toTop5Rate(barrierCounts, totalUnduplicatedClients),
        WARNING_SIGNS: toTop5Rate(warningSignCounts, totalUnduplicatedClients),
        TRIGGERS: toTop5Rate(triggerCounts, totalUnduplicatedClients),
        ACTIONS_CHOSEN: toTop5Rate(
          actionsChosenCounts,
          totalUnduplicatedClients,
        ),
        SUCCESSFUL_ACTIONS: toTop5Rate(
          successfulActionCounts,
          totalUnduplicatedClients,
        ),
        UNSUCCESSFUL_ACTIONS: toTop5Rate(
          unsuccessfulActionCounts,
          totalUnduplicatedClients,
        ),
        POS_COPING: toTop5Rate(posCopingCounts, totalUnduplicatedClients),
        NEG_COPING: toTop5Rate(negCopingCounts, totalUnduplicatedClients),
        OUTSIDE_RANGE_MH: toTop5Rate(
          outsideRangeMHCounts,
          totalUnduplicatedClients,
        ),
        OUTSIDE_RANGE_PH: toTop5Rate(
          outsideRangePHCounts,
          totalUnduplicatedClients,
        ),
        OUTSIDE_RANGE_SH: toTop5Rate(
          outsideRangeSHCounts,
          totalUnduplicatedClients,
        ),
        OUTSIDE_RANGE_SOH: toTop5Rate(
          outsideRangeSoHCounts,
          totalUnduplicatedClients,
        ),
        PHYSICAL_DX: toTop5Rate(physicalDxCounts, totalUnduplicatedClients),
        MENTAL_DX: toTop5Rate(mentalDxCounts, totalUnduplicatedClients),
      });
    } catch (err) {
      console.error("Failed to fetch Top 5 stats:", err);
      setTop5Stats({
        TRAUMAS: [],
        STRENGTHS: [],
        BARRIERS: [],
        WARNING_SIGNS: [],
        TRIGGERS: [],
        ACTIONS_CHOSEN: [],
        SUCCESSFUL_ACTIONS: [],
        UNSUCCESSFUL_ACTIONS: [],
        POS_COPING: [],
        NEG_COPING: [],
        OUTSIDE_RANGE_MH: [],
        OUTSIDE_RANGE_PH: [],
        OUTSIDE_RANGE_SH: [],
        OUTSIDE_RANGE_SOH: [],
        PHYSICAL_DX: [],
        MENTAL_DX: [],
      });
    }
  };

  const fetchRecommendationStats = async (professionalsList) => {
    try {
      const range = buildSelectedDateRange();
      // Gather all professional emails
      const professionalEmails = professionalsList.map((p) => p.email);

      // Fetch all notifications (you may need pagination if large)
      const { data } = await client.graphql({
        query: listNotifications,
        variables: { limit: 200 },
      });
      const notifications = data?.listNotifications?.items || [];

      const eligibleNotifications = notifications.filter((n) =>
        isMsWithinRange(toMs(n?.createdAt), range),
      );

      // Filter recommended changes sent by any pro under org
      const recommendedChanges = eligibleNotifications.filter(
        (n) =>
          professionalEmails.includes(n.SenderEmail) &&
          n.NotificationType === "Recommendation", // or "Recommendation" depending on your schema
      );

      // Filter accepted recommendations sent back by clients to pros
      const acceptedRecommendations = eligibleNotifications.filter(
        (n) =>
          professionalEmails.includes(n.TargetEmail) &&
          n.NotificationType === "RecommendationBack" &&
          n.RecommendationNote === "Accepted", // or however you denote acceptance
      );

      setRecommendationStats({
        recommended: recommendedChanges.length,
        accepted: acceptedRecommendations.length,
      });
    } catch (err) {
      console.error("Failed to fetch recommendation stats:", err);
      setRecommendationStats({ recommended: 0, accepted: 0 });
    }
  };

  const [plansPaid, setPlansPaid] = useState(0);
  const fetchPlansPaid = async () => {
    try {
      const today = new Date().toISOString();

      const user = await getCurrentUser();

      const orgId = user.userId;

      // Step 2: Construct full filter using AND
      const filterConditions = [
        { paidUntil: { gt: today } },
        {
          or: [
            { paymentStatus: { eq: "active" } },
            { paymentStatus: { eq: "completed" } },
          ],
        },

        { organizationId: { eq: orgId } },
      ];

      const { data } = await client.graphql({
        query: listUserPayments,
        variables: {
          filter: {
            and: filterConditions,
          },
        },
      });

      const payments = data?.listUserPayments?.items || [];

      const totalPlans = payments.reduce((sum, payment) => {
        return sum + (Number(payment.clientsProjected) || 0);
      }, 0);

      setPlansPaid(totalPlans);
    } catch (err) {
      console.error("Failed to fetch plans paid for:", err);
      setPlansPaid(0);
    }
  };

  const [stats, setStats] = useState({
    totalAssessments: 0,
    unduplicatedAssessments: 0,
    totalInProgress: 0,
    totalDiscontinued: 0,
    totalSuccessful: 0,
    assessmentsReviewed: 0,
    plansReviewed: 0,
  });

  useEffect(() => {
    // Stats should respond to filters when viewing clients.
    // If a connected client is selected, scope to them even if filters exclude them.
    const scopedClients = connectedClient?.clientId
      ? filteredClientsForStats.filter(
          (c) => c.clientId === connectedClient.clientId,
        )
      : filteredClientsForStats;

    const clientsForStats =
      connectedClient?.clientId && scopedClients.length === 0
        ? clients.filter((c) => c.clientId === connectedClient.clientId)
        : scopedClients;

    // Only fetch if we have clients
    if (clientsForStats.length > 0) {
      const loadStats = async () => {
        try {
          await fetchStats(clientsForStats);
          await fetchImprovementStats(clientsForStats);
          await fetchTop5Stats(clientsForStats);
          if (view === "clients") {
            await fetchSdoHStats(clientsForStats);
          } else {
            setSdoHStats({
              byField: {
                SDoHAgeRange: { total: 0, counts: {} },
                SDoHGender: { total: 0, counts: {} },
                SDoHMaritalStatus: { total: 0, counts: {} },
                SDoHEducation: { total: 0, counts: {} },
                SDoHIncome: { total: 0, counts: {} },
                SDoHJobStatus: { total: 0, counts: {} },
                SDoHHousingStatus: { total: 0, counts: {} },
                SDoHReligion: { total: 0, counts: {} },
              },
            });
          }
        } catch (err) {
          console.error("Error loading stats:", err);
        }
      };
      loadStats();
    } else {
      // Reset stats if no clients to show
      setStats({
        totalAssessments: 0,
        unduplicatedAssessments: 0,
        totalInProgress: 0,
        totalDiscontinued: 0,
        totalSuccessful: 0,
        assessmentsReviewed: 0,
        plansReviewed: 0,
      });
      setImprovementStats({
        CM: "--",
        MH: "--",
        PH: "--",
        SH: "--",
        SoH: "--",
        OS: "--",
      });
      setTopDiagnoses([]);
      setTopActions([]);
      setTopTraumas([]);
      setTop5Stats({
        TRAUMAS: [],
        STRENGTHS: [],
        BARRIERS: [],
        WARNING_SIGNS: [],
        TRIGGERS: [],
        ACTIONS_CHOSEN: [],
        SUCCESSFUL_ACTIONS: [],
        UNSUCCESSFUL_ACTIONS: [],
        POS_COPING: [],
        NEG_COPING: [],
        OUTSIDE_RANGE_MH: [],
        OUTSIDE_RANGE_PH: [],
        OUTSIDE_RANGE_SH: [],
        OUTSIDE_RANGE_SOH: [],
        PHYSICAL_DX: [],
        MENTAL_DX: [],
      });
      setSdoHStats({
        byField: {
          SDoHAgeRange: { total: 0, counts: {} },
          SDoHGender: { total: 0, counts: {} },
          SDoHMaritalStatus: { total: 0, counts: {} },
          SDoHEducation: { total: 0, counts: {} },
          SDoHIncome: { total: 0, counts: {} },
          SDoHJobStatus: { total: 0, counts: {} },
          SDoHHousingStatus: { total: 0, counts: {} },
          SDoHReligion: { total: 0, counts: {} },
        },
      });
    }
  }, [clients, filteredClientsForStats, connectedClient?.clientId]);

  useEffect(() => {
    if (!professionals || professionals.length === 0) return;
    fetchRecommendationStats(professionals);
  }, [professionals, startDate, endDate]);

  const fetchSdoHStats = async (clientsList) => {
    try {
      const valueCountsByField = Object.fromEntries(
        sdoHFields.map((f) => [f, {}]),
      );
      const totalsByField = Object.fromEntries(sdoHFields.map((f) => [f, 0]));

      const uniqueClients = Array.from(
        new Map(
          (clientsList || [])
            .filter((c) => c?.clientId)
            .map((c) => [c.clientId, c]),
        ).values(),
      );

      const range = buildSelectedDateRange();

      for (const user of uniqueClients) {
        const intervals =
          linkIntervalsByClientIdRef.current?.get?.(user?.clientId) || [];
        const allowedByWindow = (ms) =>
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals);

        const { data, errors } = await client.graphql({
          query: listAssessmentAnswers,
          variables: {
            filter: {
              and: [
                {
                  or: [
                    { owner: { eq: user.clientId } },
                    { userId: { eq: user.clientId } },
                  ],
                },
                {
                  or: [
                    { CompletedDate: { attributeExists: true } },
                    { isActive: { eq: false } },
                  ],
                },
              ],
            },
            limit: 1000,
          },
        });

        if (errors?.length) {
          console.error("listAssessmentAnswers (SDoH) errors:", errors);
        }

        const assessments = data?.listAssessmentAnswers?.items || [];
        const eligible = assessments.filter((a) =>
          allowedByWindow(getAssessmentLikeMs(a)),
        );
        if (eligible.length === 0) continue;

        eligible.sort(
          (a, b) =>
            (getAssessmentLikeMs(b) ?? 0) - (getAssessmentLikeMs(a) ?? 0),
        );
        const latest = eligible[0];
        for (const field of sdoHFields) {
          const raw = latest?.[field];
          const value = typeof raw === "string" ? raw.trim() : "";
          if (!value) continue;
          totalsByField[field] += 1;
          valueCountsByField[field][value] =
            (valueCountsByField[field][value] || 0) + 1;
        }
      }

      const byField = {};
      for (const field of sdoHFields) {
        byField[field] = {
          total: totalsByField[field] || 0,
          counts: valueCountsByField[field] || {},
        };
      }

      setSdoHStats({ byField });
    } catch (err) {
      console.error("Failed to fetch SDoH stats:", err);
      setSdoHStats({
        byField: {
          SDoHAgeRange: { total: 0, counts: {} },
          SDoHGender: { total: 0, counts: {} },
          SDoHMaritalStatus: { total: 0, counts: {} },
          SDoHEducation: { total: 0, counts: {} },
          SDoHIncome: { total: 0, counts: {} },
          SDoHJobStatus: { total: 0, counts: {} },
          SDoHHousingStatus: { total: 0, counts: {} },
          SDoHReligion: { total: 0, counts: {} },
        },
      });
    }
  };

  useEffect(() => {
    const searchTerms = search.toLowerCase().trim().split(/\s+/);
    const list = view === "professionals" ? professionals : clients;

    const matchesClientFilters = (item) => {
      const first = item.firstName?.toLowerCase() || "";
      const last = item.lastName?.toLowerCase() || "";
      const email = item.email?.toLowerCase() || "";
      const fullName = `${first} ${last}`;

      const matchesSearch = searchTerms.every(
        (term) =>
          first.includes(term) ||
          last.includes(term) ||
          email.includes(term) ||
          fullName.includes(term),
      );

      const matchesProFilter =
        !clientFilterPro || item.professionalEmail === clientFilterPro;

      const matchesDateFilter = (() => {
        if (!startDate && !endDate) return true;

        const range = buildSelectedDateRange();
        const intervals =
          linkIntervalsByClientIdRef.current?.get?.(item?.clientId) ||
          (item?.linkedAt || item?.createdAt
            ? [
                {
                  start: item?.linkedAt || item?.createdAt,
                  end: item?.unlinkedAt || null,
                },
              ]
            : []);
        if (!intervals || intervals.length === 0) return false;
        return intervals.some((i) => intervalOverlapsRange(i, range));
      })();

      const matchesDepartment =
        !filterDepartment ||
        (item.primaryDepartment && item.primaryDepartment === filterDepartment);
      const matchesProgram =
        !filterProgram ||
        (item.primaryProgramsUnit &&
          item.primaryProgramsUnit === filterProgram);
      const matchesService =
        !filterService ||
        (item.primaryService && item.primaryService === filterService);

      return (
        matchesSearch &&
        matchesProFilter &&
        matchesDateFilter &&
        matchesDepartment &&
        matchesProgram &&
        matchesService
      );
    };

    const filteredList = list.filter((item) => {
      // --- SEARCH FILTER ---
      const first = item.firstName?.toLowerCase() || "";
      const last = item.lastName?.toLowerCase() || "";
      const email = item.email?.toLowerCase() || "";
      const fullName = `${first} ${last}`;

      const matchesSearch = searchTerms.every(
        (term) =>
          first.includes(term) ||
          last.includes(term) ||
          email.includes(term) ||
          fullName.includes(term),
      );

      // --- PROFESSIONAL FILTER (for clients) ---
      const matchesProFilter =
        view === "clients"
          ? !clientFilterPro || item.professionalEmail === clientFilterPro
          : true;

      // --- DATE FILTER (link interval overlap) ---
      const matchesDateFilter = (() => {
        if (view !== "clients") return true;
        if (!startDate && !endDate) return true;

        const range = buildSelectedDateRange();
        const intervals =
          linkIntervalsByClientIdRef.current?.get?.(item?.clientId) ||
          (item?.linkedAt || item?.createdAt
            ? [
                {
                  start: item?.linkedAt || item?.createdAt,
                  end: item?.unlinkedAt || null,
                },
              ]
            : []);
        if (!intervals || intervals.length === 0) return false;
        return intervals.some((i) => intervalOverlapsRange(i, range));
      })();

      // --- DEPARTMENT / PROGRAM / SERVICE FILTERS ---
      const matchesDepartment =
        !filterDepartment ||
        (item.primaryDepartment && item.primaryDepartment === filterDepartment);
      const matchesProgram =
        !filterProgram ||
        (item.primaryProgramsUnit &&
          item.primaryProgramsUnit === filterProgram);
      const matchesService =
        !filterService ||
        (item.primaryService && item.primaryService === filterService);

      return (
        matchesSearch &&
        matchesProFilter &&
        matchesDateFilter &&
        matchesDepartment &&
        matchesProgram &&
        matchesService
      );
    });

    let finalList = filteredList;
    if (view === "clients" && connectedClient?.clientId) {
      const connectedOnly = filteredList.filter(
        (item) => item.clientId === connectedClient.clientId,
      );
      finalList = connectedOnly.length > 0 ? connectedOnly : filteredList;
    }

    const filteredClients = clients.filter(matchesClientFilters);
    let finalClientsForStats = filteredClients;
    if (connectedClient?.clientId) {
      const connectedOnly = filteredClients.filter(
        (item) => item.clientId === connectedClient.clientId,
      );
      finalClientsForStats =
        connectedOnly.length > 0 ? connectedOnly : filteredClients;
    }

    setFiltered(finalList);
    setFilteredClientsForStats(finalClientsForStats);
  }, [
    search,
    professionals,
    clients,
    view,
    clientFilterPro,
    startDate,
    endDate,
    filterDepartment,
    filterProgram,
    filterService,
    connectedClient,
  ]);

  const fetchOrganizationInfo = async () => {
    try {
      const { currentOrgInfo: orgInfo } = await getCurrentOrganizationContext();

      if (orgInfo) {
        setDepartments(orgInfo.Departments || []);
        setPrograms(orgInfo.ProgramsUnits || []);
        setServices(orgInfo.Services || []);
      }
    } catch (err) {
      console.error("Failed to fetch organization info:", err);
      setDepartments([]);
      setPrograms([]);
      setServices([]);
    }
  };

  const fetchProfessionals = async () => {
    try {
      const { orgIdentifierSet } = await getCurrentOrganizationContext();
      const all = await fetchAllPages({
        query: listOrganizationCPs,
        dataKey: "listOrganizationCPs",
      });
      const orgPros = all.filter(
        (cp) =>
          orgIdentifierSet.has(String(cp?.OrganizationId || "").trim()) ||
          orgIdentifierSet.has(String(cp?.owner || "").trim()),
      );

      const pros = orgPros.map((cp) => ({
        id: cp.id,
        email: cp.OrganizationCPEmail,
        firstName: cp.OrganizationCPFirstName,
        lastName: cp.OrganizationCPLastName,
        userId: cp.userId || cp.owner,
        primaryDepartment: cp.primaryDepartment || "", // <- add this
        primaryProgramsUnit: cp.primaryProgramsUnit || "", // <- add this
        primaryService: cp.primaryService || "", // <- add this
      }));

      setProfessionals(pros);
      await fetchOrganizationInfo();
      fetchAllClients(pros, orgIdentifierSet); // not just pros.map(p => p.userId)
    } catch (err) {
      console.error("Failed to load professionals:", err);
    }
  };

  const fetchAllClients = async (
    professionalsList,
    orgIdentifierSet = new Set(),
  ) => {
    try {
      const links = await fetchAllPages({
        query: listUserLinks,
        dataKey: "listUserLinks",
      });
      // Collect all organization professionals (prefer stable IDs over emails)
      const professionalUserIds = professionalsList
        .map((p) => p.userId)
        .filter(Boolean);
      const professionalIdSet = new Set(professionalUserIds);
      const professionalEmails = professionalsList
        .map((p) => String(p.email || "").toLowerCase())
        .filter(Boolean);

      // Track all link intervals per client for filtering stats by link windows.
      const intervalsByClientId = new Map();

      const eligibleLinks = links.filter((link) => {
        const professionalEmail = String(
          link.professionalEmail || "",
        ).toLowerCase();
        const linkOrgId = String(link?.organizationId || "").trim();
        const proMatch =
          (link?.professionalId &&
            professionalIdSet.has(link.professionalId)) ||
          professionalEmails.includes(professionalEmail);
        const orgMatch = linkOrgId && orgIdentifierSet.has(linkOrgId);
        if (!proMatch && !orgMatch) return false;
        const start = link?.linkedAt || link?.createdAt || null;
        if (!start) return false;
        const clientId = link?.clientId;
        if (clientId) {
          const end = link?.unlinkedAt || null;
          const arr = intervalsByClientId.get(clientId) || [];
          arr.push({ start, end });
          intervalsByClientId.set(clientId, arr);
        }
        return true;
      });

      linkIntervalsByClientIdRef.current = intervalsByClientId;

      // De-dupe clients for display (keep most recent link row per client)
      const byClientId = new Map();
      for (const link of eligibleLinks) {
        const pro = professionalsList.find(
          (p) =>
            (link?.professionalId && p.userId === link.professionalId) ||
            String(p.email || "").toLowerCase() ===
              String(link.professionalEmail || "").toLowerCase(),
        );
        const clientInfo = getUserInfoById(link.clientId) || {};

        const next = {
          id: link.id,
          clientId: link.clientId,
          email: clientInfo.Email || link.clientEmail,
          firstName: link.clientFirstName,
          lastName: link.clientLastName,
          professionalEmail: link.professionalEmail,
          professionalFirstName: link.professionalFirstName,
          professionalLastName: link.professionalLastName,
          createdAt: link.createdAt, // <- add this
          linkedAt: link.linkedAt,
          unlinkedAt: link.unlinkedAt,
          organizationId: link?.organizationId || "",
          primaryDepartment: pro?.primaryDepartment || "",
          primaryProgramsUnit: pro?.primaryProgramsUnit || "",
          primaryService: pro?.primaryService || "",
        };

        const clientId = next.clientId;
        if (!clientId) continue;
        const nextStartMs = toMs(link?.linkedAt || link?.createdAt) ?? 0;
        const prev = byClientId.get(clientId);
        const prevStartMs = prev
          ? (toMs(prev?.linkedAt || prev?.createdAt) ?? 0)
          : -1;
        if (!prev || nextStartMs >= prevStartMs) byClientId.set(clientId, next);
      }

      const allClients = Array.from(byClientId.values());

      setClients(allClients);
      setTotalClients(allClients.length);
      fetchPlansPaid();
      fetchRecommendationStats(professionalsList);
    } catch (err) {
      console.error("Failed to load client stats:", err);
    }
  };
  const fetchImprovementStats = async (clientsList) => {
    try {
      const scoreFields = [
        "CMScore",
        "MHScore",
        "PHScore",
        "SHScore",
        "SoHScore",
      ];
      const improvedCount = {
        CMScore: 0,
        MHScore: 0,
        PHScore: 0,
        SHScore: 0,
        SoHScore: 0,
        OSScore: 0,
      };

      const uniqueClientIds = new Set();
      const uniqueClientsList = [];
      for (const c of clientsList || []) {
        const id = c?.clientId;
        if (!id || uniqueClientIds.has(id)) continue;
        uniqueClientIds.add(id);
        uniqueClientsList.push(c);
      }

      let comparableClientsCount = 0;

      const range = buildSelectedDateRange();

      const getAssessmentTimestamp = (item) => {
        const completedDateRaw = (item?.CompletedDate || "").trim();
        const completedTimeRaw = (item?.CompletedTime || "").trim();

        const normalizeDate = (dateStr) => {
          // Accept YYYY-MM-DD or MM/DD/YYYY
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
          const mdy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (mdy) {
            const mm = String(mdy[1]).padStart(2, "0");
            const dd = String(mdy[2]).padStart(2, "0");
            const yyyy = mdy[3];
            return `${yyyy}-${mm}-${dd}`;
          }
          return "";
        };

        const normalizeTime = (timeStr) => {
          if (!timeStr) return "";
          // Accept HH:MM[:SS] (24h)
          if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr)) {
            return timeStr.length === 5 ? `${timeStr}:00` : timeStr;
          }

          // Accept h:mm[:ss] AM/PM
          const ampm = timeStr.match(
            /^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)\s*$/i,
          );
          if (ampm) {
            let hours = Number(ampm[1]);
            const minutes = Number(ampm[2]);
            const seconds = Number(ampm[3] || 0);
            const meridiem = String(ampm[4]).toUpperCase();
            if (meridiem === "PM" && hours < 12) hours += 12;
            if (meridiem === "AM" && hours === 12) hours = 0;
            return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
          }

          // Unknown format -> signal failure so we can fall back to updatedAt.
          return "";
        };

        const normalizedDate = normalizeDate(completedDateRaw);
        const normalizedTime = normalizeTime(completedTimeRaw);

        const updated = new Date(item?.updatedAt);
        const updatedTime = Number.isNaN(updated.getTime())
          ? 0
          : updated.getTime();
        const created = new Date(item?.createdAt);
        const createdTime = Number.isNaN(created.getTime())
          ? 0
          : created.getTime();

        if (normalizedDate && normalizedTime) {
          const d = new Date(`${normalizedDate}T${normalizedTime}`);
          const t = d.getTime();
          if (!Number.isNaN(t)) return t;
        }

        return updatedTime || createdTime || 0;
      };

      const toNumber = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
      };

      for (const c of uniqueClientsList) {
        if (!c?.clientId) continue;

        const intervals =
          linkIntervalsByClientIdRef.current?.get?.(c.clientId) || [];
        const allowedByWindow = (ms) =>
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals);

        const { data } = await client.graphql({
          query: listAssessmentScores,
          variables: {
            filter: {
              and: [
                {
                  or: [
                    { owner: { eq: c.clientId } },
                    { userId: { eq: c.clientId } },
                  ],
                },
                {
                  or: [
                    { CompletedDate: { attributeExists: true } },
                    { isActive: { eq: false } },
                  ],
                },
              ],
            },
            limit: 1000,
          },
        });

        const assessmentsRaw = data?.listAssessmentScores?.items || [];
        const assessments = assessmentsRaw.filter((a) =>
          allowedByWindow(getAssessmentTimestamp(a)),
        );

        if (assessments.length >= 2) {
          comparableClientsCount += 1;

          assessments.sort((a, b) => {
            const tA = getAssessmentTimestamp(a);
            const tB = getAssessmentTimestamp(b);
            if (tA !== tB) return tA - tB;
            const uA = new Date(a?.updatedAt).getTime();
            const uB = new Date(b?.updatedAt).getTime();
            if (!Number.isNaN(uA) && !Number.isNaN(uB) && uA !== uB)
              return uA - uB;
            const cA = new Date(a?.createdAt).getTime();
            const cB = new Date(b?.createdAt).getTime();
            if (!Number.isNaN(cA) && !Number.isNaN(cB) && cA !== cB)
              return cA - cB;
            return String(a?.id || "").localeCompare(String(b?.id || ""));
          });
          const secondLast = assessments[assessments.length - 2];
          const last = assessments[assessments.length - 1];

          for (const field of scoreFields) {
            const oldScore = toNumber(secondLast?.[field]) ?? 0;
            const newScore = toNumber(last?.[field]) ?? 0;
            if (oldScore - newScore >= 2) {
              improvedCount[field]++;
            }
          }

          const improvedCountForClient = scoreFields.filter((field) => {
            const oldScore = toNumber(secondLast?.[field]) ?? 0;
            const newScore = toNumber(last?.[field]) ?? 0;
            return oldScore - newScore >= 2;
          }).length;

          if (improvedCountForClient >= 3) {
            improvedCount.OSScore++;
          }
        }
      }

      const calculatePercent = (count) =>
        comparableClientsCount === 0
          ? "--"
          : Math.round((count / comparableClientsCount) * 100) + "%";

      setImprovementStats({
        CM: calculatePercent(improvedCount.CMScore),
        MH: calculatePercent(improvedCount.MHScore),
        PH: calculatePercent(improvedCount.PHScore),
        SH: calculatePercent(improvedCount.SHScore),
        SoH: calculatePercent(improvedCount.SoHScore),
        OS: calculatePercent(improvedCount.OSScore),
      });
    } catch (err) {
      console.error("Failed to calculate improvement stats:", err);
      setImprovementStats({
        CM: "--",
        MH: "--",
        PH: "--",
        SH: "--",
        SoH: "--",
        OS: "--",
      });
    }
  };

  const fetchStats = async (clientsList) => {
    try {
      let totalAssessments = 0;
      let unduplicatedAssessments = 0;
      let totalInProgress = 0;
      let totalDiscontinued = 0;
      let totalSuccessful = 0;
      let assessmentsReviewed = 0;
      let plansReviewed = 0;

      const range = buildSelectedDateRange();

      const allReviews = [];
      let nextToken = null;
      do {
        const res = await client.graphql({
          query: listProfessionalReviews,
          variables: { limit: 1000, nextToken },
        });
        const items = res?.data?.listProfessionalReviews?.items || [];
        allReviews.push(...items);
        nextToken = res?.data?.listProfessionalReviews?.nextToken || null;
      } while (nextToken);

      const assessmentReviewedIds = new Set(
        allReviews
          .filter((r) => r?.ReviewType === "assessment" && r?.ReviewedId)
          .map((r) => r.ReviewedId),
      );
      const planReviewedIds = new Set(
        allReviews
          .filter((r) => r?.ReviewType === "health plan" && r?.ReviewedId)
          .map((r) => r.ReviewedId),
      );

      for (const c of clientsList) {
        const clientId = c?.clientId;
        if (!clientId) continue;
        const intervals =
          linkIntervalsByClientIdRef.current?.get?.(clientId) || [];

        const [assessmentsRes, plansRes, createPlansRes] = await Promise.all([
          client.graphql({
            query: listAssessmentAnswers,
            variables: {
              filter: {
                and: [
                  {
                    or: [
                      { owner: { eq: clientId } },
                      { userId: { eq: clientId } },
                    ],
                  },
                  {
                    or: [
                      { CompletedDate: { attributeExists: true } },
                      { isActive: { eq: false } },
                    ],
                  },
                ],
              },
              limit: 1000,
            },
          }),
          client.graphql({
            query: listTrackPlans,
            variables: {
              filter: {
                or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
              },
              limit: 1000,
            },
          }),
          client.graphql({
            query: listCreatePlans,
            variables: {
              filter: {
                userId: { eq: clientId },
              },
              limit: 1000,
            },
          }),
        ]);

        if (assessmentsRes?.errors?.length) {
          console.error(
            "listAssessmentAnswers (Stats) errors:",
            assessmentsRes.errors,
          );
        }
        if (plansRes?.errors?.length) {
          console.error("listTrackPlans (Stats) errors:", plansRes.errors);
        }

        const assessmentsRaw =
          assessmentsRes?.data?.listAssessmentAnswers?.items || [];
        const plansRaw = plansRes?.data?.listTrackPlans?.items || [];
        const createPlansRaw =
          createPlansRes?.data?.listCreatePlans?.items || [];

        const allowedByWindow = (ms) =>
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals);

        const assessments = assessmentsRaw.filter((a) =>
          allowedByWindow(getAssessmentLikeMs(a)),
        );
        const plans = plansRaw.filter((p) =>
          allowedByWindow(toMs(p?.createdAt)),
        );
        const createPlans = createPlansRaw.filter((p) =>
          allowedByWindow(toMs(p?.createdAt)),
        );

        totalAssessments += assessments.length;
        if (assessments.length > 0) unduplicatedAssessments += 1;
        assessmentsReviewed += assessments.filter((a) =>
          assessmentReviewedIds.has(a?.id),
        ).length;
        plansReviewed += createPlans.filter((p) =>
          planReviewedIds.has(p?.id),
        ).length;
        for (const plan of plans) {
          if (plan.Discontinued) totalDiscontinued++;
          else if (plan.Successful) totalSuccessful++;
          else totalInProgress++;
        }
      }

      setStats({
        totalAssessments,
        unduplicatedAssessments,
        totalInProgress,
        totalDiscontinued,
        totalSuccessful,
        assessmentsReviewed,
        plansReviewed,
      });
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      setStats({
        totalAssessments: 0,
        unduplicatedAssessments: 0,
        totalInProgress: 0,
        totalDiscontinued: 0,
        totalSuccessful: 0,
        assessmentsReviewed: 0,
        plansReviewed: 0,
      });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 20,
      }}
    >
      {connectedClient && (
        <div
          style={{
            width: "90%",
            maxWidth: "1000px",
            padding: "12px 20px",
            backgroundColor: "#d9eaff",
            border: "2px solid #0067B3",
            borderRadius: 8,
            marginBottom: 10,
            textAlign: "center",
            fontWeight: "bold",
            color: "#0067B3",
          }}
        >
          Currently Viewing: {connectedClient.firstName}{" "}
          {connectedClient.lastName}
          <button
            onClick={() => setConnectedClient(null)}
            style={{
              marginLeft: 16,
              padding: "6px 12px",
              backgroundColor: "#8C1456",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            View All Clients
          </button>
        </div>
      )}
      {/* GENERAL STATS */}
      <div style={containerStyle2}>
        <h3 style={titleStyle}>General</h3>
        <div style={statsRow}>
          <div style={statLine}>
            Plans Paid For
            <div style={highlightNumber}>{plansPaid}</div>
          </div>
          <div style={statLine}>
            Total Clients
            <div style={highlightNumber}>{totalClients}</div>
          </div>
          <div style={statLine}>
            Available Clients
            <div style={highlightNumber}>
              {Math.max(0, plansPaid - totalClients)}
            </div>
          </div>
          <div style={statLine}>
            Active Professionals
            <div style={highlightNumber}>{professionals.length}</div>
          </div>
          <div style={statLine}>
            Active Clients
            <div style={highlightNumber}>
              {connectedClient?.clientId
                ? 1
                : clients.filter((client) => !client.unlinkedAt).length}
            </div>
          </div>
        </div>
      </div>

      {/* TOP 5 BUTTONS */}
      <div style={containerStyle2}>
        <h3 style={titleStyle}>Top 5 Stats</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
            columnGap: 10,
            rowGap: 0,
            alignItems: "start",
          }}
        >
          {TOP5_SECTIONS.flatMap(({ key, label }, idx) => {
            const tile = (
              <div
                key={key}
                style={{ minWidth: 0, paddingTop: 30, paddingBottom: 30 }}
              >
                <button
                  onClick={() => toggleTop5Section(key)}
                  style={{
                    cursor: "pointer",
                    padding: "12px 12px",
                    borderRadius: 12,
                    border: openTop5Sections?.[key]
                      ? "2px solid #0067B3"
                      : "1px solid #ccc",
                    backgroundColor: openTop5Sections?.[key]
                      ? "#d9eaff"
                      : "#fff",
                    color: "#0067B3",
                    fontWeight: 600,
                    width: "100%",
                    fontSize: 14,
                    textAlign: "center",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  {label}
                </button>

                {openTop5Sections?.[key] && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      marginTop: 10,
                      textAlign: "left",
                    }}
                  >
                    {(top5Stats?.[key] || []).length === 0 ? (
                      <div
                        style={{ ...statLineAligned, alignItems: "flex-start" }}
                      >
                        No Data Available
                      </div>
                    ) : (
                      (top5Stats?.[key] || []).map((item) => (
                        <div
                          key={item.label}
                          style={{
                            fontSize: 14,
                            color: "#0067B3",
                            fontWeight: "normal",
                            padding: "2px 0",
                          }}
                        >
                          {item.label} ({formatTop5Count(key, item.count)})
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );

            if (!isMobile && (key === "OUTSIDE_RANGE_SOH" || idx === 7)) {
              return [
                tile,
                <div
                  key={`${key}-divider`}
                  style={{
                    gridColumn: "1 / -1",
                    height: 1,
                    backgroundColor: "#ccc",
                    width: "100%",
                    justifySelf: "stretch",
                  }}
                />,
              ];
            }

            if (!isMobile && idx === 3) {
              return [
                tile,
                <div
                  key="row1-divider"
                  style={{
                    gridColumn: "1 / -1",
                    height: 1,
                    backgroundColor: "#ccc",
                    width: "100%",
                    justifySelf: "stretch",
                  }}
                />,
              ];
            }

            return tile;
          })}
        </div>

        <div
          style={{
            marginTop: 24,
            fontSize: 12,
            color: "#0067B3",
            textAlign: "center",
          }}
        >
          Note: This section uses each client's most recent assessment and plan
          within the selected date range (unduplicated).
        </div>
      </div>

      {/* MIDDLE & RIGHT STATS BOX */}
      <div style={statsContainerResponsive}>
        {/* MIDDLE STATS */}
        <div style={sideBoxStyle}>
          <h3 style={titleStyle}>
            {connectedClient
              ? `Stats for: ${connectedClient.firstName} ${connectedClient.lastName}`
              : "Plan and Progress Stats (All Clients)"}
          </h3>
          <div style={statsRowAligned}>
            <div style={statLineAligned}>
              Active Plans
              <div style={highlightNumberAligned}>{stats.totalInProgress}</div>
            </div>
            <div style={statLineAligned}>
              Recommendations Made
              <div style={highlightNumberAligned}>
                {recommendationStats.recommended}
              </div>
            </div>
            <div style={statLineAligned}>
              Successful Actions
              <div style={highlightNumberAligned}>{stats.totalSuccessful}</div>
            </div>
            <div style={statLineAligned}>
              Discontinued Actions
              <div style={highlightNumberAligned}>
                {stats.totalDiscontinued}
              </div>
            </div>
            <div style={statLineAligned}>
              Assessments Reviewed
              <div style={highlightNumberAligned}>
                {stats.assessmentsReviewed}
              </div>
            </div>
            <div style={statLineAligned}>
              Plans Reviewed
              <div style={highlightNumberAligned}>{stats.plansReviewed}</div>
            </div>
          </div>
        </div>

        {/* RIGHT STATS */}
        <div style={sideBoxStyle}>
          <h3 style={titleStyle}>
            {connectedClient
              ? "Client Improvement"
              : "Client Improvements (All Clients)"}
          </h3>
          <div style={statsRowAligned}>
            <div style={statLineAligned}>
              Assessments Completed
              <div style={highlightNumberAligned}>{stats.totalAssessments}</div>
            </div>
            <div style={statLineAligned}>
              Unduplicated Assessments
              <div style={highlightNumberAligned}>
                {stats.unduplicatedAssessments}
              </div>
            </div>
            <div style={statLineAligned}>
              Coping Improvement
              <div style={highlightNumberAligned}>{improvementStats.CM}</div>
            </div>
            <div style={statLineAligned}>
              Mental Improvement
              <div style={highlightNumberAligned}>{improvementStats.MH}</div>
            </div>
            <div style={statLineAligned}>
              Physical Improvement
              <div style={highlightNumberAligned}>{improvementStats.PH}</div>
            </div>
            <div style={statLineAligned}>
              Spiritual Improvement
              <div style={highlightNumberAligned}>{improvementStats.SH}</div>
            </div>
            <div style={statLineAligned}>
              Social Improvement
              <div style={highlightNumberAligned}>{improvementStats.SoH}</div>
            </div>
            <div style={statLineAligned}>
              Overall Score
              <div style={highlightNumberAligned}>{improvementStats.OS}</div>
            </div>
          </div>
        </div>
      </div>

      {view === "clients" && (
        <div style={containerStyle2}>
          <h3 style={titleStyle}>Social Determinants of Health</h3>
          <div
            style={{
              ...statsRowAligned,
              gridTemplateColumns: isMobile
                ? "repeat(2, minmax(170px, 1fr))"
                : "repeat(4, minmax(170px, 1fr))",
              justifyItems: "center",
            }}
          >
            {sdoHPieOrder.map((field, idx) => {
              const label = sdoHDisplayNames[field] || field;
              const fieldStats = sdoHStats?.byField?.[field] || {
                total: 0,
                counts: {},
              };
              const segments = buildPieSegments(fieldStats.counts, 6);

              const tile = (
                <div
                  key={field}
                  style={{ ...statLineAligned, alignItems: "stretch" }}
                >
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#0067B3",
                      textAlign: "center",
                    }}
                  >
                    {label}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    <div
                      aria-label={`${label} distribution pie chart`}
                      style={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        ...pieStyleFromSegments(segments),
                      }}
                    />

                    {segments.length === 0 ? (
                      <div
                        style={{
                          color: "#0067B3",
                          fontSize: 14,
                          textAlign: "center",
                        }}
                      >
                        No Data Available
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "inline-block",
                          maxWidth: 230,
                          margin: "0 auto",
                          textAlign: "left",
                        }}
                      >
                        {segments.map((s) => (
                          <div
                            key={s.label}
                            style={{
                              display: "flex",
                              justifyContent: "flex-start",
                              gap: 10,
                              fontSize: 13,
                              color: "#0067B3",
                              marginTop: 6,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: 2,
                                  backgroundColor: s.color,
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {s.label}
                              </span>
                            </div>
                            <span style={{ fontWeight: 600 }}>
                              {s.percent.toFixed(0)}%
                            </span>
                          </div>
                        ))}
                        <div
                          style={{
                            marginTop: 10,
                            fontSize: 12,
                            color: "#0067B3",
                            textAlign: "center",
                          }}
                        >
                          n = {fieldStats.total}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );

              const divider =
                !isMobile && idx === 3 ? (
                  <div
                    key={`${field}-divider`}
                    style={{
                      gridColumn: "1 / -1",
                      height: 1,
                      backgroundColor: "#ccc",
                      width: "100%",
                      justifySelf: "stretch",
                      marginTop: 12,
                      marginBottom: 12,
                    }}
                  />
                ) : null;

              return [tile, divider];
            })}
          </div>
        </div>
      )}

      {/* LIST SECTION */}
      <div style={containerStyle2}>
        <h3 style={titleStyle}>Filter Information</h3>
        {connectedClient?.clientId && view === "clients" && (
          <div
            style={{
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontWeight: "bold", color: "#0067B3" }}>
              Connected Client: {connectedClient.firstName}{" "}
              {connectedClient.lastName}
            </span>
            <button
              style={disconnectBtn}
              onClick={() => setConnectedClient(null)}
            >
              Stop Viewing Connected Client
            </button>
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          {/* Search input (fills row) */}
          <input
            type="text"
            placeholder="Filter by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              ...inputStyle,
              flex: 1,
              minWidth: isMobile ? "100%" : 260,
              marginBottom: 0,
            }}
          />

          {/* Start date */}
          <input
            type="date"
            value={pendingStartDate}
            onChange={(e) => setPendingStartDate(e.target.value)}
            style={{ ...inputStyle, width: 160, marginBottom: 0 }}
          />

          {/* End date */}
          <input
            type="date"
            value={pendingEndDate}
            onChange={(e) => setPendingEndDate(e.target.value)}
            style={{ ...inputStyle, width: 160, marginBottom: 0 }}
          />

          <button
            onClick={() => {
              setStartDate(pendingStartDate);
              setEndDate(pendingEndDate);
            }}
            style={{
              backgroundColor: "#0067B3",
              color: "#fff",
              border: "none",
              padding: "10px 14px",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: "bold",
              marginTop: 6,
            }}
          >
            Apply Filters
          </button>
          {/* Department filter */}
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            style={{ ...inputStyle, marginBottom: 0 }}
          >
            <option value="">All Departments</option>
            {departments.map((dep) => (
              <option key={dep} value={dep}>
                {dep}
              </option>
            ))}
          </select>

          {/* Program/Unit filter */}
          <select
            value={filterProgram}
            onChange={(e) => setFilterProgram(e.target.value)}
            style={{ ...inputStyle, marginBottom: 0 }}
          >
            <option value="">All Programs/Units</option>
            {programs.map((prog) => (
              <option key={prog} value={prog}>
                {prog}
              </option>
            ))}
          </select>

          {/* Service filter */}
          <select
            value={filterService}
            onChange={(e) => setFilterService(e.target.value)}
            style={{ ...inputStyle, width: 200, marginBottom: 0 }}
          >
            <option value="">All Services</option>
            {services.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {view === "clients" && (
          <select
            value={clientFilterPro}
            onChange={(e) => setClientFilterPro(e.target.value)}
            style={{ ...inputStyle, maxWidth: "100%", marginBottom: 12 }}
          >
            <option value="">All Professionals</option>
            {professionals
              .slice()
              .sort((a, b) => a.firstName.localeCompare(b.firstName))
              .map((pro) => (
                <option key={pro.email} value={pro.email}>
                  {pro.firstName} {pro.lastName}
                </option>
              ))}
          </select>
        )}

        <div style={linksSectionStyle}>
          {filtered.length > 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ color: "#0067B3", fontSize: 14 }}>
                Showing{" "}
                {Math.min(
                  (safeUserListPage - 1) * userListPageSize + 1,
                  filtered.length,
                )}
                -
                {Math.min(safeUserListPage * userListPageSize, filtered.length)}{" "}
                of {filtered.length}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <label
                  style={{
                    color: "#0067B3",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  Per page
                  <select
                    value={userListPageSize}
                    onChange={(e) => {
                      setUserListPageSize(Number(e.target.value));
                      setUserListPage(1);
                    }}
                    style={{
                      ...inputStyle,
                      width: 88,
                      margin: 0,
                      padding: "8px 10px",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <button
                  onClick={() =>
                    setUserListPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={safeUserListPage === 1}
                  style={{
                    ...buttonStyle,
                    backgroundColor: "#6b7280",
                    padding: "8px 12px",
                    opacity: safeUserListPage === 1 ? 0.6 : 1,
                  }}
                >
                  Previous
                </button>
                <div style={{ color: "#0067B3", fontSize: 14 }}>
                  Page {safeUserListPage} of {totalUserListPages}
                </div>
                <button
                  onClick={() =>
                    setUserListPage((prev) =>
                      Math.min(totalUserListPages, prev + 1),
                    )
                  }
                  disabled={safeUserListPage === totalUserListPages}
                  style={{
                    ...buttonStyle,
                    backgroundColor: "#6b7280",
                    padding: "8px 12px",
                    opacity: safeUserListPage === totalUserListPages ? 0.6 : 1,
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}

          {filtered.length === 0 ? (
            <p style={emptyStyle}>No entries found.</p>
          ) : (
            paginatedFiltered.map((item) => (
              <div
                key={item.id}
                style={{
                  ...linkBoxStyle,
                  cursor: view === "clients" ? "pointer" : "default",
                  border:
                    view === "clients" &&
                    connectedClient?.clientId === item.clientId
                      ? "2px solid #0067B3"
                      : "2px solid transparent",
                }}
                onClick={() => {
                  if (view !== "clients") return;
                  setConnectedClient((prev) =>
                    prev?.clientId === item.clientId ? null : item,
                  );
                }}
              >
                <div style={linkRowStyle}>
                  <div style={userColumnStyle}>
                    <strong>First Name</strong>
                    <div>{item.firstName}</div>
                  </div>
                  <div style={userColumnStyle}>
                    <strong>Last Name</strong>
                    <div>{item.lastName}</div>
                  </div>
                  <div style={userColumnStyle}>
                    <strong>Email</strong>
                    <div>{item.email}</div>
                  </div>
                  {view === "clients" && (
                    <div style={userColumnStyle}>
                      <strong>Professional</strong>
                      <div>
                        {item.professionalFirstName} {item.professionalLastName}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const topBarStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  backgroundColor: "#eaf4ff",
  padding: "10px 24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  zIndex: 1000,
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};

const disconnectBtn = {
  backgroundColor: "#8C1456",
  color: "#fff",
  border: "none",
  padding: "6px 12px",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: "bold",
};

const titleStyle = {
  fontSize: 20,
  fontWeight: "bold",
  marginBottom: 16,
  color: "#0067B3",
  alignItems: "center",
};

const inputStyle = {
  width: "100%",
  padding: 10,
  fontSize: 14,
  borderRadius: 8,
  border: "1.5px solid #ccc",
  marginTop: 6,
  marginBottom: 20,
  boxSizing: "border-box",
};

const linksSectionStyle = {
  marginTop: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const linkBoxStyle = {
  backgroundColor: "#f9f9f9",
  padding: 16,
  borderRadius: 10,
  marginBottom: 0,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
};

const linkRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
};

const userColumnStyle = {
  flex: 1,
  textAlign: "center",
};

const emptyStyle = {
  fontStyle: "italic",
  color: "#333",
};

const statsBoxStyle = {
  flexDirection: "column",
  alignItems: "center",
  width: "1000px",
  margin: "1px auto",
  padding: 24,
  borderRadius: 12,
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  display: "block",
};

const statsRow = {
  display: "flex",
  flexDirection: "row", // side by side
  justifyContent: "center",
  gap: 24, // spacing between each stat
  flexWrap: "wrap", // wrap if screen is too small
};

// Used specifically for the two side-by-side dashboard stat boxes to keep
// stat blocks aligned regardless of label length and count.
const statsRowAligned = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 24,
  alignItems: "start",
};

const statLine = {
  display: "flex",
  flexDirection: "column", // label on top, number below
  alignItems: "center",
  fontSize: 14,
  color: "#0067B3",
};

const statLineAligned = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  fontSize: 14,
  color: "#0067B3",
  width: "100%",
};

const highlightNumber = {
  display: "block",
  textAlign: "center",
  borderRadius: 12,
  color: "#0067B3",
  fontSize: 18,
  fontWeight: 600,
  padding: "6px 12px",
  marginTop: 6,
  marginBottom: 6,
  minWidth: 120,
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};

const highlightNumberAligned = {
  display: "block",
  textAlign: "center",
  borderRadius: 12,
  color: "#0067B3",
  fontSize: 18,
  fontWeight: 600,
  padding: "6px 12px",
  marginTop: 6,
  marginBottom: 6,
  width: "100%",
  minWidth: "unset",
  boxSizing: "border-box",
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};
const buttonStyle = {
  backgroundColor: "#0067B3",
  color: "white",
  fontWeight: "bold",
  padding: "8px 16px",
  borderRadius: 6,
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};
const statsContainer = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  width: "1000px",
  margin: "24px auto",
  gap: 24,
};

const miniList = {
  margin: "8px 0 0 16px",
  padding: 0,
  fontSize: 14,
  lineHeight: 1.4,
  listStyleType: "disc",
};
